import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';

const locationFaqs = [
  {
    question: 'Why hire BitNinja Technologies for software development in San Francisco?',
    answer: 'BitNinja brings the best of both worlds: a San Francisco office for in-person collaboration and a 20+ person engineering team in India for competitive pricing. You get Silicon Valley-quality software without Silicon Valley costs. Our SF office at 456 Innovation Drive is available for face-to-face meetings, project kickoffs, and ongoing consultations.',
  },
  {
    question: 'What types of software does BitNinja build for San Francisco companies?',
    answer: 'We build SaaS platforms, AI/ML-powered applications, fintech solutions, healthcare software, e-commerce platforms, mobile apps, enterprise dashboards, and custom internal tools. Our SF clients range from seed-stage startups to Series C companies who need reliable, scalable engineering without the overhead of hiring in-house.',
  },
  {
    question: 'How does the San Francisco + India model work for software projects?',
    answer: 'Our SF office handles client relationships, requirements gathering, architecture decisions, and project management. Our Surat engineering team handles development, QA, and deployment. We use Slack, Zoom, Jira, and GitHub for seamless communication. The timezone overlap (IST morning = PST evening) means our team is working while you sleep — you wake up to progress every day.',
  },
  {
    question: 'What is the pricing for software development through BitNinja San Francisco?',
    answer: 'Our pricing is significantly more competitive than hiring a local SF engineering team. We offer fixed-price projects starting at $4,999 for web applications and monthly retainer models for ongoing development. Every project gets a detailed, no-obligation quote after a free consultation. We bill in USD and accept wire transfers and credit cards.',
  },
  {
    question: 'Can I meet the BitNinja team in person in San Francisco?',
    answer: 'Yes, schedule an in-person meeting at our San Francisco office: 456 Innovation Drive, Suite 300, San Francisco, CA 94105. We are available for project kickoffs, sprint reviews, and strategic planning sessions. For day-to-day communication, we use video calls and Slack to keep things efficient.',
  },
  {
    question: 'What industries does BitNinja serve in the Bay Area?',
    answer: 'We have built software for Bay Area startups and enterprises in fintech, healthtech, SaaS, e-commerce, proptech, edtech, and AI/ML. Our engineers understand the fast-paced, product-driven culture of Silicon Valley and ship features at startup speed with enterprise-grade reliability.',
  },
  {
    question: 'How fast can BitNinja start on a new San Francisco project?',
    answer: 'Most projects kick off within one week of contract signing. We start with a discovery workshop (in-person at our SF office or via Zoom), define the MVP scope, and begin sprint one immediately. Our agile process means you see working software within the first two weeks.',
  },
];

const locationJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "BitNinja Technologies — San Francisco",
    "image": "https://storage.readdy-site.link/project_files/932d0adb-bf56-44ff-a11c-0aa982709dd7/0499b76b-9ae9-4384-9aac-227cd2d8a42c_compressed_BITNINJA-final-image.webp",
    "url": "https://bitninjatech.com/san-francisco",
    "telephone": "+91 9033779369",
    "email": "info@bitninjatech.com",
    "description": "BitNinja Technologies San Francisco office — custom software development, SaaS platforms, mobile apps, AI/ML solutions, and IT consulting for Bay Area startups and enterprises.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "456 Innovation Drive, Suite 300",
      "addressLocality": "San Francisco",
      "addressRegion": "CA",
      "postalCode": "94105",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 37.7749,
      "longitude": -122.4194
    },
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "09:00", "closes": "18:00" }
    ],
    "areaServed": ["San Francisco", "Bay Area", "California", "United States", "Worldwide"],
    "priceRange": "$$$"
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://bitninjatech.com" },
      { "@type": "ListItem", "position": 2, "name": "San Francisco Office", "item": "https://bitninjatech.com/san-francisco" }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": locationFaqs.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const LocationSFPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', service: '', message: '' });
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'message') setCharCount(value.length);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHead
        title="San Francisco Software Development | Custom Apps & SaaS | BitNinja Technologies"
        description="BitNinja Technologies offers custom software development, SaaS platforms, mobile apps, and IT consulting in San Francisco, CA. Bay Area startups and enterprises — get a free quote today."
        canonicalUrl="https://bitninjatech.com/san-francisco"
        jsonLd={locationJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 md:pt-32 overflow-hidden bg-foreground-900 min-h-[520px] md:min-h-[560px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20tech%20office%20in%20San%20Francisco%20with%20Bay%20Bridge%20view%20through%20floor%20to%20ceiling%20windows%20clean%20minimalist%20workspace%20programmers%20at%20standing%20desks%20warm%20ambient%20lighting%20sleek%20furniture%20soft%20golden%20hour%20light%20over%20the%20city%20professional%20atmosphere%20editorial%20photography%20high%20detail&width=1600&height=900&seq=sf-hero-2026&orientation=landscape"
            alt="BitNinja Technologies San Francisco Office"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>
        <div className="section-padding relative z-10 w-full py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-background-300 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-primary-400">San Francisco Office</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-primary-500/20 border border-primary-500/30 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></span>
              <span className="text-xs font-mono uppercase tracking-widest text-primary-300">US Office</span>
            </div>
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight text-background-50 mb-4 max-w-4xl">
              Software Development in{' '}
              <span className="text-primary-400">San Francisco, CA</span>
            </h1>
            <p className="text-background-200 text-lg md:text-xl mb-8 max-w-2xl">
              Silicon Valley-grade software engineering — without the Silicon Valley price. Our SF office brings BitNinja&apos;s engineering excellence to Bay Area startups and enterprises.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact-form" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                Get a Free Quote
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
              <a href="#services-overview" className="inline-flex items-center gap-2 bg-background-50/10 backdrop-blur-sm hover:bg-background-50/20 text-background-50 px-6 py-3.5 rounded-full text-sm font-semibold border border-background-50/20 transition-colors cursor-pointer whitespace-nowrap">
                Our Services
                <i className="ri-arrow-down-line"></i>
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-background-300">
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-line text-primary-400"></i>
                <span>456 Innovation Drive, Suite 300, San Francisco, CA 94105</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-phone-line text-primary-400"></i>
                <span>+91 9033779369</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="bg-primary-500 border-t border-primary-400/20">
        <div className="section-padding py-8">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '50+', label: 'Projects Delivered' },
              { value: '15+', label: 'Bay Area Clients' },
              { value: '98%', label: 'Client Satisfaction' },
              { value: '24h', label: 'Follow-the-Sun Dev' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="font-display text-3xl md:text-4xl font-bold text-background-50 mb-1">{stat.value}</div>
                <div className="text-xs md:text-sm text-background-50/70 uppercase tracking-wider font-mono">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About SF Presence */}
      <section className="py-14 md:py-16 bg-background-50 border-t border-background-200">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-16">
              <div className="lg:w-1/2">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex w-6 h-6 items-center justify-center bg-foreground-900 text-background-50 rounded-sm">
                    <i className="ri-building-line text-xs"></i>
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-600">US Office</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-bold leading-[1.02] tracking-tight text-foreground-900 mb-6">
                  Bay Area Software{' '}
                  <span className="text-primary-500">Engineering.</span>
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  Our San Francisco office at 456 Innovation Drive puts BitNinja&apos;s engineering team within reach of the world&apos;s most demanding tech market. We opened our SF presence to give Bay Area startups and enterprises a local partner who understands Silicon Valley speed, culture, and quality standards.
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  The SF office handles client strategy, architecture, project management, and in-person collaboration — while our 20+ person engineering team in India executes around the clock. This dual-shore model means you get continuous progress: your developers work while you sleep, and you wake up to new features.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  For Bay Area companies, this translates to faster time-to-market, significantly lower engineering costs, and a partner who is available for face-to-face meetings whenever you need them. No outsourcing black box — just transparent, collaborative software development.
                </p>
              </div>
              <div className="lg:w-1/2">
                <div className="rounded-2xl overflow-hidden border border-background-200 h-80 lg:h-96">
                  <img
                    src="https://readdy.ai/api/search-image?query=Bright%20modern%20tech%20office%20meeting%20room%20in%20San%20Francisco%20with%20large%20windows%20overlooking%20city%20skyline%20white%20walls%20minimalist%20furniture%20people%20collaborating%20around%20a%20table%20with%20laptops%20natural%20daylight%20professional%20atmosphere%20warm%20wood%20accents%20indoor%20plants%20high%20detail%20editorial%20style&width=1200&height=800&seq=sf-office-meeting&orientation=landscape"
                    alt="BitNinja San Francisco meeting room"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section id="services-overview" className="py-14 md:py-16 bg-background-100 border-t border-background-200">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="inline-flex w-6 h-6 items-center justify-center bg-foreground-900 text-background-50 rounded-sm">
                  <i className="ri-code-line text-xs"></i>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-600">What We Build</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-[1.02] tracking-tight text-foreground-900">
                Software That Scales.{' '}
                <span className="text-primary-500">From Day One.</span>
              </h2>
              <p className="mt-4 text-foreground-600 max-w-2xl mx-auto text-[15px] leading-relaxed">
                Startups to Series C — our engineering team builds products that handle growth from MVP to millions of users.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: 'ri-cloud-line', title: 'SaaS Platform Development', desc: 'Multi-tenant SaaS architectures with React, Node.js, and AWS. Subscription management, user onboarding, analytics dashboards — the complete package.' },
                { icon: 'ri-brain-line', title: 'AI & ML Integration', desc: 'LLM-powered features, recommendation engines, NLP pipelines, and computer vision. We integrate AI into your existing products or build AI-first applications.' },
                { icon: 'ri-smartphone-line', title: 'Mobile App Development', desc: 'Native iOS and Android apps, plus React Native cross-platform solutions. Built for performance, designed for conversion, deployed to App Store and Google Play.' },
                { icon: 'ri-bank-line', title: 'Fintech & Payments', desc: 'Payment processing, digital wallets, trading platforms, and compliance-heavy financial software. PCI DSS aware, SOC 2 ready.' },
                { icon: 'ri-heart-pulse-line', title: 'Healthtech Solutions', desc: 'HIPAA-compliant healthcare platforms, telemedicine apps, patient portals, and medical device integrations. Secure, reliable, compliant.' },
                { icon: 'ri-shopping-cart-2-line', title: 'E-Commerce & Marketplaces', desc: 'Custom Shopify Plus stores, headless commerce, multi-vendor marketplaces. Optimized for conversion with Stripe and Shopify integrations.' },
              ].map((s, i) => (
                <div key={i} className="bg-background-50 rounded-2xl p-6 border border-background-200 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-foreground-900 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <i className={`${s.icon} text-xl text-background-50`}></i>
                  </div>
                  <h3 className="font-display font-bold text-foreground-900 mb-2 text-sm">{s.title}</h3>
                  <p className="text-foreground-600 text-xs leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Dual-Shore Model */}
      <section className="py-14 md:py-16 bg-foreground-900 border-t border-foreground-800">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="inline-flex w-6 h-6 items-center justify-center bg-primary-500 text-background-50 rounded-sm">
                  <i className="ri-global-line text-xs"></i>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background-400">How We Work</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-background-50">
                The Best of Both{' '}
                <span className="text-primary-500">Coasts.</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-user-location-line', title: 'SF Office = Your HQ', desc: 'Face-to-face meetings, sprint reviews, and strategy sessions in SoMa. We are local when you need us to be.' },
                { icon: 'ri-sun-line', title: 'Follow-the-Sun Dev', desc: 'Our India team codes while you sleep. You start each morning with fresh commits, completed features, and a progress report.' },
                { icon: 'ri-money-dollar-circle-line', title: 'SF Quality, Better Value', desc: 'Same engineering standards as top SF agencies — at a fraction of the cost. Our dual-shore model cuts your dev spend by 40-60%.' },
                { icon: 'ri-shield-check-line', title: 'Enterprise Security', desc: 'SOC 2 compliant processes, encrypted communication, NDAs standard. Your IP and data stay protected across both offices.' },
              ].map((item, i) => (
                <div key={i} className="bg-background-50/5 border border-background-50/10 rounded-2xl p-6 hover:border-primary-500/40 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-xl text-background-50`}></i>
                  </div>
                  <h3 className="font-display font-bold text-background-50 mb-2 text-sm">{item.title}</h3>
                  <p className="text-background-400 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-14 md:py-16 bg-background-100 border-t border-background-200">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground-900">
                Tech Stack.{' '}
                <span className="text-primary-500">Production-Ready.</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {['React', 'Next.js', 'TypeScript', 'Python', 'Node.js', 'PostgreSQL', 'AWS Lambda', 'Docker', 'Kubernetes', 'TensorFlow', 'GraphQL', 'Redis'].map((tech, i) => (
                <div key={i} className="bg-background-50 border border-background-200 rounded-xl px-5 py-4 text-center hover:border-foreground-900/30 transition-all cursor-default">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-foreground-700">{tech}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-16 bg-background-50 border-t border-background-200">
        <div className="section-padding">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="inline-flex w-6 h-6 items-center justify-center bg-foreground-900 text-background-50 rounded-sm">
                  <i className="ri-question-answer-line text-xs"></i>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-600">FAQ</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground-900">
                San Francisco Software Dev{' '}
                <span className="text-primary-500">FAQs.</span>
              </h2>
            </div>
            <div className="space-y-3">
              {locationFaqs.map((item, i) => (
                <details key={i} className="group bg-background-100 rounded-2xl border border-background-200 overflow-hidden transition-all duration-300">
                  <summary className="flex items-center justify-between p-5 md:p-6 cursor-pointer list-none">
                    <h3 className="font-display text-base md:text-lg font-bold text-foreground-900 pr-4 group-open:text-primary-500 transition-colors">{item.question}</h3>
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 border border-background-200 flex-shrink-0 group-open:bg-foreground-900 group-open:border-foreground-900 transition-all">
                      <i className="ri-add-line text-foreground-700 group-open:text-background-50 group-open:hidden"></i>
                      <i className="ri-subtract-line text-background-50 hidden group-open:block"></i>
                    </span>
                  </summary>
                  <div className="px-5 md:px-6 pb-5 md:pb-6">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form + Map */}
      <section id="contact-form" className="py-14 md:py-16 bg-foreground-900 border-t border-foreground-800">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="inline-flex w-6 h-6 items-center justify-center bg-primary-500 text-background-50 rounded-sm">
                  <i className="ri-send-plane-line text-xs"></i>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background-400">Start Your Project</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-background-50">
                Ready to Ship?{' '}
                <span className="text-primary-500">Let&apos;s Talk.</span>
              </h2>
              <p className="mt-4 text-background-300 max-w-xl mx-auto text-[15px]">
                Describe your software project. Our SF team will schedule a free consultation — in person or via Zoom — within one business day.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
              <div className="lg:col-span-7 bg-background-50 rounded-2xl p-7 md:p-9">
                <form
                  data-readdy-form
                  action="https://readdy.ai/api/form/d9168tgbhec1he3oppc0"
                  method="POST"
                >
                  <input tabIndex={-1} autoComplete="off" aria-hidden="true" className="nc-decoy-field" name="contact_alt" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">Full Name</label>
                      <input id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors" placeholder="Alex Chen" />
                    </div>
                    <div>
                      <label htmlFor="email" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">Work Email</label>
                      <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors" placeholder="alex@startup.io" />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">Phone <span className="text-foreground-400 normal-case">(optional)</span></label>
                      <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors" placeholder="+1 (415) 555-0123" />
                    </div>
                    <div>
                      <label htmlFor="company" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">Company <span className="text-foreground-400 normal-case">(optional)</span></label>
                      <input id="company" name="company" value={formData.company} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors" placeholder="Your Company" />
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="service" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">Service Interested In</label>
                      <select id="service" name="service" value={formData.service} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors">
                        <option value="">Select a service</option>
                        <option value="SaaS Platform Development">SaaS Platform Development</option>
                        <option value="AI & ML Integration">AI &amp; ML Integration</option>
                        <option value="Mobile App Development">Mobile App Development</option>
                        <option value="Fintech & Payments">Fintech &amp; Payments</option>
                        <option value="Healthtech Solutions">Healthtech Solutions</option>
                        <option value="E-Commerce & Marketplaces">E-Commerce &amp; Marketplaces</option>
                        <option value="IT Consulting">IT Consulting</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="message" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500">Your Message</label>
                      <span className="font-mono text-[10px] text-foreground-500">{charCount} / 500</span>
                    </div>
                    <textarea id="message" name="message" rows={5} maxLength={500} required value={formData.message} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors resize-none" placeholder="Tell us about your product, team, timeline, budget — anything that helps us understand your needs..."></textarea>
                  </div>
                  <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <p className="text-xs text-foreground-500 max-w-sm">By submitting, you agree to BitNinja SF contacting you about your inquiry. We never sell or share your details.</p>
                    <button type="submit" className="inline-flex items-center justify-center gap-2 bg-foreground-900 hover:bg-foreground-800 text-background-50 text-sm font-semibold px-6 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                      Send Inquiry
                      <i className="ri-arrow-right-up-line text-base"></i>
                    </button>
                  </div>
                </form>
              </div>
              <div className="lg:col-span-5 flex flex-col gap-6">
                <div className="bg-background-50/[0.04] border border-background-50/15 rounded-2xl p-6 space-y-4">
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0"><i className="ri-map-pin-line text-base"></i></span>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-background-400 mb-1">Visit Us</div>
                      <div className="text-sm text-background-50">456 Innovation Drive, Suite 300,<br />San Francisco, CA 94105</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0"><i className="ri-phone-line text-base"></i></span>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-background-400 mb-1">Call Us</div>
                      <div className="text-sm text-background-50">+91 9033779369</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0"><i className="ri-mail-line text-base"></i></span>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-background-400 mb-1">Email</div>
                      <div className="text-sm text-background-50">info@bitninjatech.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0"><i className="ri-time-line text-base"></i></span>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-background-400 mb-1">Hours</div>
                      <div className="text-sm text-background-50">Mon–Fri, 9:00 AM – 6:00 PM PST</div>
                    </div>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden border border-background-50/15 h-56">
                  <iframe
                    title="BitNinja Technologies San Francisco Office"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3153.2!2d-122.4194!3d37.7749!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085808f5e5e5e5e%3A0x1!2s456%20Innovation%20Drive%2C%20San%20Francisco%2C%20CA%2094105!5e0!3m2!1sen!2sus!4v1712000000000!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LocationSFPage;